import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const SignupVerificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [form, setForm] = useState({
    signupVerificationEnabled: "disabled",
    signupVerificationChannels: "email,whatsapp",
    signupVerificationDefaultChannel: "email",
    signupVerificationCodeTtlMinutes: "10",
    signupVerificationResendSeconds: "60",
    signupVerificationMaxAttempts: "5",
    signupSmtpHost: "",
    signupSmtpPort: "587",
    signupSmtpSecure: "false",
    signupSmtpUser: "",
    signupSmtpPass: "",
    signupSmtpPassConfigured: false,
    signupSmtpFrom: "",
    signupSmtpFromName: "",
    signupWhatsappApiUrl: "",
    signupWhatsappApiToken: "",
    signupWhatsappApiTokenConfigured: false,
    signupWhatsappMessageTemplate:
      "Seu código de confirmação é {{code}}. Ele expira em {{minutes}} minutos."
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/signup-verification/settings");
      setForm((prev) => ({
        ...prev,
        ...data,
        signupSmtpPass: "",
        signupWhatsappApiToken: ""
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const channels = String(form.signupVerificationChannels || "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);

  const toggleChannel = (channel) => {
    const set = new Set(channels);
    if (set.has(channel)) set.delete(channel);
    else set.add(channel);
    const next = Array.from(set);
    if (!next.length) {
      toast.error("Selecione ao menos um canal");
      return;
    }
    setField("signupVerificationChannels", next.join(","));
    if (!next.includes(form.signupVerificationDefaultChannel)) {
      setField("signupVerificationDefaultChannel", next[0]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.signupSmtpPassConfigured;
      delete payload.signupWhatsappApiTokenConfigured;
      if (!payload.signupSmtpPass) delete payload.signupSmtpPass;
      if (!payload.signupWhatsappApiToken) delete payload.signupWhatsappApiToken;
      const { data } = await api.put("/auth/signup-verification/settings", payload);
      setForm((prev) => ({
        ...prev,
        ...data,
        signupSmtpPass: "",
        signupWhatsappApiToken: ""
      }));
      toast.success("Configurações de verificação salvas");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await api.post("/auth/signup-verification/test-email", { to: testEmail });
      toast.success("E-mail de teste enviado");
    } catch (err) {
      toastError(err);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestWhatsapp = async () => {
    setTestingWhatsapp(true);
    try {
      await api.post("/auth/signup-verification/test-whatsapp", { phone: testPhone });
      toast.success("WhatsApp de teste enviado");
    } catch (err) {
      toastError(err);
    } finally {
      setTestingWhatsapp(false);
    }
  };

  if (loading) {
    return <Typography>Carregando verificação de cadastro...</Typography>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={form.signupVerificationEnabled === "enabled"}
              onChange={(e) =>
                setField(
                  "signupVerificationEnabled",
                  e.target.checked ? "enabled" : "disabled"
                )
              }
            />
          }
          label="Exigir código de confirmação no cadastro"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Canais permitidos (o cliente escolhe no cadastro)
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          <Button
            variant={channels.includes("email") ? "contained" : "outlined"}
            color="primary"
            onClick={() => toggleChannel("email")}
            style={{ textTransform: "none" }}
          >
            E-mail
          </Button>
          <Button
            variant={channels.includes("whatsapp") ? "contained" : "outlined"}
            color="primary"
            onClick={() => toggleChannel("whatsapp")}
            style={{ textTransform: "none" }}
          >
            WhatsApp
          </Button>
        </Box>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6 }}>
          Deixe os dois ativos para o cliente poder escolher. WhatsApp exige URL + token da API abaixo.
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          label="Canal padrão"
          value={form.signupVerificationDefaultChannel}
          onChange={(e) => setField("signupVerificationDefaultChannel", e.target.value)}
        >
          {channels.map((c) => (
            <MenuItem key={c} value={c}>
              {c === "email" ? "E-mail" : "WhatsApp"}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Validade do código (min)"
          value={form.signupVerificationCodeTtlMinutes}
          onChange={(e) => setField("signupVerificationCodeTtlMinutes", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Reenvio (segundos)"
          value={form.signupVerificationResendSeconds}
          onChange={(e) => setField("signupVerificationResendSeconds", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Máx. tentativas"
          value={form.signupVerificationMaxAttempts}
          onChange={(e) => setField("signupVerificationMaxAttempts", e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 8 }}>
          SMTP
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Host SMTP"
          value={form.signupSmtpHost}
          onChange={(e) => setField("signupSmtpHost", e.target.value)}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Porta"
          value={form.signupSmtpPort}
          onChange={(e) => setField("signupSmtpPort", e.target.value)}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          label="SSL/TLS"
          value={form.signupSmtpSecure}
          onChange={(e) => setField("signupSmtpSecure", e.target.value)}
        >
          <MenuItem value="false">STARTTLS / sem SSL</MenuItem>
          <MenuItem value="true">SSL (465)</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Usuário SMTP"
          value={form.signupSmtpUser}
          onChange={(e) => setField("signupSmtpUser", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type="password"
          label={
            form.signupSmtpPassConfigured
              ? "Senha SMTP (deixe em branco para manter)"
              : "Senha SMTP"
          }
          value={form.signupSmtpPass}
          onChange={(e) => setField("signupSmtpPass", e.target.value)}
          autoComplete="new-password"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="E-mail remetente"
          value={form.signupSmtpFrom}
          onChange={(e) => setField("signupSmtpFrom", e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="Nome remetente"
          value={form.signupSmtpFromName}
          onChange={(e) => setField("signupSmtpFromName", e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 8 }}>
          API WhatsApp (código)
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Use a Messages API externa, sem depender de conexão ativa na empresa 1.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="URL da API"
          value={form.signupWhatsappApiUrl}
          onChange={(e) => setField("signupWhatsappApiUrl", e.target.value)}
          placeholder="https://sua-api.com.br/api/messages/send"
          helperText="Endpoint completo da Messages API que envia o código."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type="password"
          label={
            form.signupWhatsappApiTokenConfigured
              ? "Token da conexão (deixe em branco para manter)"
              : "Token da conexão (Bearer)"
          }
          value={form.signupWhatsappApiToken}
          onChange={(e) => setField("signupWhatsappApiToken", e.target.value)}
          autoComplete="new-password"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          multiline
          minRows={2}
          label="Template da mensagem"
          value={form.signupWhatsappMessageTemplate}
          onChange={(e) => setField("signupWhatsappMessageTemplate", e.target.value)}
          helperText="Use {{code}} e {{minutes}}"
        />
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar verificação"}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="E-mail para teste"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
        <Button
          style={{ marginTop: 8, textTransform: "none" }}
          variant="outlined"
          color="primary"
          disabled={testingEmail || !testEmail}
          onClick={handleTestEmail}
        >
          {testingEmail ? "Enviando..." : "Testar e-mail"}
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label="WhatsApp para teste"
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
          helperText="Com DDI, ex: 5511999999999"
        />
        <Button
          style={{ marginTop: 8, textTransform: "none" }}
          variant="outlined"
          color="primary"
          disabled={testingWhatsapp || !testPhone}
          onClick={handleTestWhatsapp}
        >
          {testingWhatsapp ? "Enviando..." : "Testar WhatsApp"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default SignupVerificationSettings;
